import { formatMessage } from '@99designs/common/i18n';
import { Category } from '__generated__/Category';
import {
    ProjectBriefFields_inspirationFiles,
    ProjectBriefFields_referenceFiles,
} from '__generated__/ProjectBriefFields';
import { Uploader } from '__generated__/Uploader';
import Text from 'common/components/Text';
import DescriptionField from 'projects/components/BriefFields/DescriptionField';
import InspirationFilesField from 'projects/components/BriefFields/InspirationFilesField';
import InspirationUrlsField from 'projects/components/BriefFields/InspirationUrlsField';
import LanguageField from 'projects/components/BriefFields/LanguageField';
import ReferenceFilesField from 'projects/components/BriefFields/ReferenceFilesField';
import TitleField from 'projects/components/BriefFields/TitleField';
import CategorySelect from 'projects/components/CategorySelect';
import { ObservableField as Field, ObservableForm } from 'projects/components/ObservableForm';
import { MAX_TITLE_LENGTH } from 'projects/consts';
import { trackViewedBriefEvent } from 'projects/events';
import React from 'react';

interface Fields {
    language: string;
    title: string;
    category: string;
    description: string;
    usage: string;
    referenceFiles: ProjectBriefFields_referenceFiles[];
    inspirationFiles: ProjectBriefFields_inspirationFiles[];
    inspirationUrls: string[];
}

export interface Props<T> {
    form: ObservableForm<T>;
    categories: Category[];
    brief: {
        id: string;
        uploader: Uploader;
    };
}

class DetailsPage<T extends Fields> extends React.Component<Props<T>> {
    componentDidMount() {
        trackViewedBriefEvent({
            id: this.props.brief.id,
            page: 'Details',
        });
    }

    render() {
        const { categories, form, brief } = this.props;
        return (
            <div className="content-setting">
                <LanguageField form={form} />
                <TitleField
                    form={form}
                    placeholderText={formatMessage(
                        __(
                            'E.g. Packaging for an organic juice company (max {maxSize} characters)'
                        ),
                        { maxSize: MAX_TITLE_LENGTH }
                    )}
                />

                <Field
                    form={form}
                    name="category"
                    title={__('What type of design do you need?')}
                    tooltip={__('Select the design category that fits your needs best.')}
                    required={true}
                    render={() => (
                        <CategorySelect
                            {...form.attach('category')}
                            placeholder={__('Select category')}
                            categories={categories}
                            size="max"
                        />
                    )}
                />

                <DescriptionField
                    form={form}
                    placeholderText={__(
                        'E.g. I need a package designed for a new flavor of organic juice. It should feature a retro and vibrant design style and include our company logo on the front of the bottle. Our juice appeals to adults aged from 25-35. Our bottle contains 500mls (see attached reference image for actual shape and size).'
                    )}
                />

                <Field
                    form={form}
                    name="usage"
                    title={__('How will your design be used?')}
                    tooltip={__(
                        'Tell us about your product so the designer can get an idea of the files they need to create (for print and/or digital).'
                    )}
                    render={() => (
                        <Text
                            placeholder={__('E.g. Billboard, Facebook campaign, bookcover etc.')}
                            fill={true}
                            {...form.attach('usage')}
                        />
                    )}
                />

                <ReferenceFilesField form={form} uploader={brief.uploader} />
                <InspirationFilesField form={form} uploader={brief.uploader} />
                <InspirationUrlsField form={form} />
            </div>
        );
    }
}

export default DetailsPage;
